import { isDefined } from "@clipboard-health/util-ts";
import { ModalStatus, useModalState } from "@src/lib/ui-react";
import { type ReactNode, useCallback, useMemo, useState } from "react";

import { useApproveFcmRequest, useRejectFcmRequest } from "../api/useFcmRequestActions";
import { useGetShiftBasicDetails } from "../api/useGetShiftBasicDetails";
import { FCM_APPROVAL_STATUS } from "../utils/parseFcmRequestMessage";
import { type FcmRequestActionIntent, FcmRequestDialogContext } from "./FcmRequestDialogContext";
import { FcmApprovalDialog } from "./FcmApprovalDialog";
import { FcmNoShowDialog } from "./FcmNoShowDialog";
import { FcmRejectionDialog } from "./FcmRejectionDialog";
import { FcmWleDialog } from "./FcmWleDialog";

type FcmRequestDialogKind = "approval" | "noShow" | "rejection" | "workerLeftEarly";
type ActiveFcmRequestDialog = Omit<FcmRequestActionIntent, "action"> & {
  kind: FcmRequestDialogKind;
};

interface Props {
  children: ReactNode;
}

export function FcmRequestDialogHost(props: Props) {
  const { children } = props;
  const [activeDialog, setActiveDialog] = useState<ActiveFcmRequestDialog>();
  const openDialog = useCallback((intent: FcmRequestActionIntent) => {
    const { action, ...requestContext } = intent;

    setActiveDialog({
      ...requestContext,
      kind: action === "approve" ? "approval" : "rejection",
    });
  }, []);
  const closeDialog = useCallback(() => setActiveDialog(undefined), []);
  const value = useMemo(() => ({ openDialog }), [openDialog]);

  return (
    <FcmRequestDialogContext.Provider value={value}>
      {children}
      {isDefined(activeDialog) && (
        <FcmActiveRequestDialog
          dialog={activeDialog}
          onChange={setActiveDialog}
          onClose={closeDialog}
        />
      )}
    </FcmRequestDialogContext.Provider>
  );
}

interface FcmActiveRequestDialogProps {
  dialog: ActiveFcmRequestDialog;
  onChange: (dialog: ActiveFcmRequestDialog) => void;
  onClose: () => void;
}

function FcmActiveRequestDialog(props: FcmActiveRequestDialogProps) {
  const { dialog, onChange, onClose } = props;
  const modalState = useModalState(ModalStatus.OPEN);
  const { data: shiftResponse, isError: isShiftDetailsError } = useGetShiftBasicDetails(
    { shiftId: dialog.shiftId },
    {
      // The message row already started this query. Observe its cache/in-flight result
      // without issuing a second request when the dialog host mounts.
      refetchOnMount: false,
    }
  );
  const { mutateAsync: approveRequest, isLoading: isApproving } = useApproveFcmRequest({
    workerId: dialog.workerId,
    workplaceId: dialog.workplaceId,
    workplaceUserId: dialog.workplaceUserId,
  });
  const { mutateAsync: rejectRequest, isLoading: isRejecting } = useRejectFcmRequest({
    workerId: dialog.workerId,
    workplaceId: dialog.workplaceId,
    workplaceUserId: dialog.workplaceUserId,
  });
  const isUpdateResponse = dialog.approvalStatus !== FCM_APPROVAL_STATUS.PENDING;
  const shift = shiftResponse?.response;
  const hasWorkerEnteredGeofence = isDefined(shift)
    ? isDefined(shift.attendance?.arrivedFacilityGeofenceAt) || isDefined(shift.clockInOut?.start)
    : undefined;
  const hasLeftGeofence = isDefined(shift?.attendance?.arrivedFacilityGeofenceAt)
    ? isDefined(shift?.attendance?.leftFacilityGeofenceAt)
    : undefined;
  const timeZone = shift?.facility?.tmz ?? dialog.timeZone;

  async function handleApprove(reason: string) {
    await approveRequest({
      shiftId: dialog.shiftId,
      messageId: dialog.messageId,
      isUpdateResponse,
      reason,
    });
    onClose();
  }

  async function handleReject(reason: string, wantsFollowUp: boolean) {
    await rejectRequest({
      shiftId: dialog.shiftId,
      messageId: dialog.messageId,
      isUpdateResponse,
      reason,
    });

    if (!wantsFollowUp || !isDefined(shift)) {
      onClose();
      return;
    }

    onChange({
      ...dialog,
      kind: hasWorkerEnteredGeofence ? "workerLeftEarly" : "noShow",
    });
  }

  switch (dialog.kind) {
    case "approval":
      return (
        <FcmApprovalDialog
          modalState={modalState}
          isUpdateResponse={isUpdateResponse}
          isLoading={isApproving}
          onApprove={handleApprove}
          onCancel={onClose}
        />
      );
    case "rejection":
      return (
        <FcmRejectionDialog
          modalState={modalState}
          isLoading={isRejecting || isApproving}
          workerName={dialog.workerName}
          hasWorkerEnteredGeofence={hasWorkerEnteredGeofence}
          hasShiftDetailsError={isShiftDetailsError && !isDefined(shift)}
          onReject={handleReject}
          onApprove={handleApprove}
          onCancel={onClose}
        />
      );
    case "noShow":
      return (
        <FcmNoShowDialog
          modalState={modalState}
          shiftId={dialog.shiftId}
          workerId={dialog.workerId ?? ""}
          workplaceId={dialog.workplaceId}
          shiftStart={shift?.start}
          canRequestReplacement={shift?.canRequestReplacement}
          geofenceEntryTime={shift?.attendance?.arrivedFacilityGeofenceAt ?? undefined}
          timezone={timeZone}
          onSuccess={onClose}
          onCancel={onClose}
        />
      );
    case "workerLeftEarly":
      return (
        <FcmWleDialog
          modalState={modalState}
          shiftId={dialog.shiftId}
          hasLeftGeofence={hasLeftGeofence}
          timezone={timeZone}
          onSuccess={onClose}
          onCancel={onClose}
        />
      );
  }
}
